/**
 * useAuth Hook
 * Provides authentication state and actions
 */

import { useCallback } from 'react';
import { useAuthStore, useIsAuthenticated } from '@/common/stores';
import type { UserRole } from '@/common/stores';
import { hasPermission, hasRole, canAccess, hasAllPermissions, hasAnyPermission } from './rbac';

/**
 * useAuth hook
 * Provides auth state and actions with permission checking
 */
export function useAuth() {
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useIsAuthenticated();
  const isLoading = useAuthStore((s) => s.isLoading);
  const error = useAuthStore((s) => s.error);
  const loginError = useAuthStore((s) => s.loginError);
  const registerError = useAuthStore((s) => s.registerError);

  const login = useAuthStore((s) => s.login);
  const register = useAuthStore((s) => s.register);
  const logout = useAuthStore((s) => s.logout);
  const refreshAccessToken = useAuthStore((s) => s.refreshAccessToken);
  const getCurrentUser = useAuthStore((s) => s.getCurrentUser);
  const updateProfile = useAuthStore((s) => s.updateProfile);

  // Permission checking helpers
  const checkPermission = useCallback(
    (permissionName: string): boolean => {
      if (!user) return false;
      return hasPermission(user.role, permissionName);
    },
    [user]
  );

  const checkRole = useCallback(
    (role: UserRole | UserRole[]): boolean => {
      if (!user) return false;
      return hasRole(user.role, role);
    },
    [user]
  );

  const checkAccess = useCallback(
    (requiredRoles: UserRole | UserRole[]): boolean => {
      return canAccess(user?.role ?? null, requiredRoles);
    },
    [user]
  );

  const checkAllPermissions = useCallback(
    (permissions: string[]): boolean => {
      if (!user) return false;
      return hasAllPermissions(user.role, permissions);
    },
    [user]
  );

  const checkAnyPermission = useCallback(
    (permissions: string[]): boolean => {
      if (!user) return false;
      return hasAnyPermission(user.role, permissions);
    },
    [user]
  );

  return {
    // State
    user,
    isAuthenticated,
    isLoading,
    error,
    loginError,
    registerError,

    // Actions
    login,
    register,
    logout,
    refreshAccessToken,
    getCurrentUser,
    updateProfile,

    // Permission helpers
    checkPermission,
    checkRole,
    checkAccess,
    checkAllPermissions,
    checkAnyPermission,
  };
}

/**
 * Hook to check if user is authenticated
 */
export function useIsAuthenticatedUser(): boolean {
  return useIsAuthenticated();
}

/**
 * Hook to get current user
 */
export function useCurrentUser() {
  return useAuthStore((s) => s.user);
}

/**
 * Hook to check if user has role
 */
export function useHasRole(role: UserRole | UserRole[]): boolean {
  const user = useAuthStore((s) => s.user);
  if (!user) return false;
  return hasRole(user.role, role);
}

/**
 * Hook to check if user has permission
 */
export function useHasPermission(permissionName: string): boolean {
  const user = useAuthStore((s) => s.user);
  if (!user) return false;
  return hasPermission(user.role, permissionName);
}
